import React from 'react';
import { Mail, MapPin, Building2, Hash, Globe, FileText, ShieldCheck, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

/**
 * Dedicated /contact page — Meta's verified-business crawler explicitly
 * looks for a stand-alone Contact/About page that lists legal name,
 * Companies House number, registered address and contact email.
 * Footer references aren't enough on their own.
 *
 * Also doubles as the public face of the company for any customer who
 * needs to find these details for invoicing, compliance, ICO etc.
 */
const Contact = () => {
  return (
    <div className="min-h-screen bg-[#fdfaf6] flex flex-col">
      <Navbar />
      <main className="flex-1 max-w-5xl mx-auto w-full px-5 lg:px-8 py-12 lg:py-20">
        {/* Page header */}
        <div className="mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-100 border border-orange-200 text-orange-700 text-xs font-semibold">
            <Sparkles className="w-3.5 h-3.5" />
            Get in touch with the team behind MyBusinessAdPro
          </div>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-tight">
            Contact <span className="bg-gradient-to-r from-orange-500 to-rose-500 bg-clip-text text-transparent">us</span>
          </h1>
          <p className="mt-4 text-base sm:text-lg text-slate-600 max-w-2xl">
            MyBusinessAdPro is operated by <strong className="text-slate-900">Krinal Ahir Limited</strong>,
            a UK-registered private limited company. Use any of the details below to get in touch — we usually
            reply within one working day.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-5">
          {/* PRIMARY CONTACT CARD */}
          <div className="md:col-span-2 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 shadow-sm">
            <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-orange-500" /> Company details
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              These are the official details for our parent legal entity, used for compliance, invoicing,
              and platform verification (Meta, Google, Stripe, ICO).
            </p>

            <dl className="mt-6 space-y-5" itemScope itemType="https://schema.org/Organization">
              <div className="flex items-start gap-3">
                <Building2 className="w-5 h-5 text-slate-400 mt-0.5 flex-shrink-0" />
                <div>
                  <dt className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Registered company name</dt>
                  <dd className="mt-0.5 text-slate-900 font-semibold" itemProp="legalName" data-testid="company-legal-name">
                    Krinal Ahir Limited
                  </dd>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Hash className="w-5 h-5 text-slate-400 mt-0.5 flex-shrink-0" />
                <div>
                  <dt className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Companies House number</dt>
                  <dd className="mt-0.5 text-slate-900 font-semibold" data-testid="company-number">
                    11940942
                    {' · '}
                    <a
                      href="https://find-and-update.company-information.service.gov.uk/company/11940942"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-orange-600 hover:underline font-medium"
                    >
                      View on Companies House &rarr;
                    </a>
                  </dd>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-slate-400 mt-0.5 flex-shrink-0" />
                <div>
                  <dt className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Registered office address</dt>
                  <dd className="mt-0.5 text-slate-900 font-semibold not-italic" data-testid="company-address" itemProp="address" itemScope itemType="https://schema.org/PostalAddress">
                    <span itemProp="streetAddress">The Spinney, 12 Scothern Lane</span><br />
                    <span itemProp="addressLocality">Sudbrooke</span>, <span itemProp="addressCountry">United Kingdom</span><br />
                    <span itemProp="postalCode">LN2 2QT</span>
                  </dd>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-slate-400 mt-0.5 flex-shrink-0" />
                <div>
                  <dt className="text-xs uppercase tracking-wider text-slate-500 font-semibold">General contact email</dt>
                  <dd className="mt-0.5 text-slate-900 font-semibold" data-testid="company-email">
                    <a href="mailto:contact@mybusinessadpro.co.uk" itemProp="email" className="text-orange-600 hover:underline">
                      contact@mybusinessadpro.co.uk
                    </a>
                  </dd>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Globe className="w-5 h-5 text-slate-400 mt-0.5 flex-shrink-0" />
                <div>
                  <dt className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Website</dt>
                  <dd className="mt-0.5 text-slate-900 font-semibold">
                    <a href="https://mybusinessadpro.co.uk" itemProp="url" className="text-orange-600 hover:underline">
                      mybusinessadpro.co.uk
                    </a>
                  </dd>
                </div>
              </div>
            </dl>
          </div>

          {/* SIDEBAR — direct routing to specialised teams */}
          <div className="space-y-4">
            <a
              href="mailto:contact@mybusinessadpro.co.uk"
              className="block p-5 rounded-2xl border border-slate-200 bg-white hover:border-orange-300 hover:shadow-md transition-all group"
              data-testid="contact-general-card"
            >
              <Mail className="w-5 h-5 text-orange-500" />
              <div className="mt-3 font-bold text-slate-900 group-hover:text-orange-600">General enquiries</div>
              <div className="mt-1 text-xs text-slate-500">contact@mybusinessadpro.co.uk</div>
            </a>
            <a
              href="mailto:billing@mybusinessadpro.co.uk"
              className="block p-5 rounded-2xl border border-slate-200 bg-white hover:border-orange-300 hover:shadow-md transition-all group"
              data-testid="contact-billing-card"
            >
              <FileText className="w-5 h-5 text-orange-500" />
              <div className="mt-3 font-bold text-slate-900 group-hover:text-orange-600">Billing &amp; invoices</div>
              <div className="mt-1 text-xs text-slate-500">billing@mybusinessadpro.co.uk</div>
            </a>
            <a
              href="mailto:abuse@mybusinessadpro.co.uk"
              className="block p-5 rounded-2xl border border-slate-200 bg-white hover:border-orange-300 hover:shadow-md transition-all group"
              data-testid="contact-abuse-card"
            >
              <ShieldCheck className="w-5 h-5 text-orange-500" />
              <div className="mt-3 font-bold text-slate-900 group-hover:text-orange-600">Trust &amp; safety</div>
              <div className="mt-1 text-xs text-slate-500">abuse@mybusinessadpro.co.uk</div>
            </a>
          </div>
        </div>

        {/* Compliance / legal section — Meta + ICO crawlers like to see this */}
        <div className="mt-10 bg-slate-900 text-white rounded-2xl p-6 sm:p-8">
          <div className="flex items-start gap-4">
            <ShieldCheck className="w-6 h-6 text-emerald-400 flex-shrink-0 mt-1" />
            <div>
              <h3 className="text-lg font-bold">Compliance &amp; regulatory</h3>
              <p className="mt-2 text-sm text-slate-300 leading-relaxed">
                Krinal Ahir Limited is registered in England &amp; Wales (Companies House
                <span className="text-white font-semibold"> 11940942</span>). MyBusinessAdPro is our trading name.
                We are the Data Controller for personal data we hold about you under UK GDPR, and the Data Processor for
                any business-customer data you process via the platform — see our{' '}
                <Link to="/legal/dpa" className="text-emerald-300 hover:underline">Data Processing Agreement</Link>{' '}
                for details.
              </p>
              <p className="mt-3 text-sm text-slate-300 leading-relaxed">
                All AI-generated advertising content is reviewed against the UK ASA&apos;s CAP Code — see our{' '}
                <Link to="/legal/asa" className="text-emerald-300 hover:underline">UK ASA Compliance</Link>{' '}
                page for the specifics.
              </p>
            </div>
          </div>
        </div>

        {/* Quick links to legal docs — establishes the trust signal trail Meta wants */}
        <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          <Link to="/legal/terms" className="px-4 py-3 rounded-lg bg-white border border-slate-200 hover:border-orange-300 transition-colors text-slate-700 hover:text-orange-600 font-medium text-center">
            Terms of Service
          </Link>
          <Link to="/legal/privacy" className="px-4 py-3 rounded-lg bg-white border border-slate-200 hover:border-orange-300 transition-colors text-slate-700 hover:text-orange-600 font-medium text-center">
            Privacy Policy
          </Link>
          <Link to="/legal/cookies" className="px-4 py-3 rounded-lg bg-white border border-slate-200 hover:border-orange-300 transition-colors text-slate-700 hover:text-orange-600 font-medium text-center">
            Cookie Policy
          </Link>
          <Link to="/legal" className="px-4 py-3 rounded-lg bg-white border border-slate-200 hover:border-orange-300 transition-colors text-slate-700 hover:text-orange-600 font-medium text-center">
            All Legal Docs
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Contact;
